<template>
  <div
    ref="sliderRef"
    class="ui-slider"
    :class="sliderClasses"
    @mousedown="onSliderMouseDown"
    @touchstart="onSliderTouchStart"
    @keydown="onKeyDown"
    :tabindex="disabled ? -1 : tabindex"
    role="slider"
    :aria-valuemin="min"
    :aria-valuemax="max"
    :aria-valuenow="modelValue"
    :aria-valuetext="ariaValueText"
    :aria-disabled="disabled"
    :aria-label="ariaLabel"
    :aria-orientation="orientation"
  >
    <!-- Track -->
    <div class="ui-slider-track">
      <!-- Range (filled portion) -->
      <div
        class="ui-slider-range"
        :style="rangeStyle"
      ></div>
    </div>
    
    <!-- Handle -->
    <div
      ref="handleRef"
      class="ui-slider-handle"
      :class="handleClasses"
      :style="handleStyle"
      @mousedown="onHandleMouseDown"
      @touchstart="onHandleTouchStart"
      @focus="onHandleFocus"
      @blur="onHandleBlur"
      :tabindex="disabled ? -1 : 0"
    >
      <!-- Handle inner circle for better visual feedback -->
      <div class="ui-slider-handle-inner"></div>
    </div>
    
    <!-- Value tooltip (optional) -->
    <div
      v-if="showTooltip"
      class="ui-slider-tooltip"
      :style="tooltipStyle"
    >
      {{ formattedValue }}
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

export default {
  name: 'UISlider',
  props: {
    modelValue: {
      type: Number,
      default: 0
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    step: {
      type: Number,
      default: 1
    },
    orientation: {
      type: String,
      default: 'horizontal',
      validator: value => ['horizontal', 'vertical'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    tabindex: {
      type: Number,
      default: 0
    },
    ariaLabel: {
      type: String,
      default: null
    },
    ariaLabelledby: {
      type: String,
      default: null
    },
    format: {
      type: Function,
      default: null
    },
    showTooltip: {
      type: Boolean,
      default: false
    },
    tooltipTemplate: {
      type: Function,
      default: null
    }
  },
  emits: ['update:modelValue', 'change', 'slidestart', 'slideend'],
  setup(props, { emit }) {
    const sliderRef = ref(null)
    const handleRef = ref(null)
    const isDragging = ref(false)
    const isFocused = ref(false)
    const startPosition = ref(0)
    const startValue = ref(0)

    // Computed properties
    const sliderClasses = computed(() => {
      return [
        'ui-slider-root',
        `ui-slider-${props.orientation}`,
        {
          'ui-slider-disabled': props.disabled,
          'ui-slider-readonly': props.readonly,
          'ui-slider-dragging': isDragging.value,
          'ui-slider-focused': isFocused.value
        }
      ]
    })

    const handleClasses = computed(() => {
      return [
        'ui-slider-handle-root',
        {
          'ui-slider-handle-active': isDragging.value,
          'ui-slider-handle-focused': isFocused.value
        }
      ]
    })

    // Calculate the percentage of the current value
    const valuePercentage = computed(() => {
      const range = props.max - props.min
      if (range === 0) return 0
      return Math.max(0, Math.min(100, ((props.modelValue - props.min) / range) * 100))
    })

    // Style for the filled range
    const rangeStyle = computed(() => {
      if (props.orientation === 'horizontal') {
        return {
          width: `${valuePercentage.value}%`
        }
      } else {
        return {
          height: `${valuePercentage.value}%`,
          bottom: '0'
        }
      }
    })

    // Style for the handle position
    const handleStyle = computed(() => {
      if (props.orientation === 'horizontal') {
        return {
          left: `${valuePercentage.value}%`,
          transform: 'translateX(-50%)'
        }
      } else {
        return {
          bottom: `${valuePercentage.value}%`,
          transform: 'translateY(50%)'
        }
      }
    })

    // Style for tooltip position
    const tooltipStyle = computed(() => {
      if (props.orientation === 'horizontal') {
        return {
          left: `${valuePercentage.value}%`,
          transform: 'translateX(-50%)'
        }
      } else {
        return {
          bottom: `${valuePercentage.value}%`,
          transform: 'translateY(50%)'
        }
      }
    })

    // Formatted value for display
    const formattedValue = computed(() => {
      if (props.tooltipTemplate) {
        return props.tooltipTemplate(props.modelValue)
      }
      if (props.format) {
        return props.format(props.modelValue)
      }
      return props.modelValue.toLocaleString()
    })

    // Aria value text
    const ariaValueText = computed(() => {
      return formattedValue.value
    })

    // Helper functions
    const normalizeValue = (value) => {
      // Ensure value is within bounds
      let normalizedValue = Math.max(props.min, Math.min(props.max, value))
      
      // Snap to step
      if (props.step > 0) {
        const steps = Math.round((normalizedValue - props.min) / props.step)
        normalizedValue = props.min + (steps * props.step)
      }
      
      return normalizedValue
    }

    const updateValue = (newValue) => {
      const normalizedValue = normalizeValue(newValue)
      if (normalizedValue !== props.modelValue) {
        emit('update:modelValue', normalizedValue)
        emit('change', { value: normalizedValue })
      }
    }

    const getValueFromPosition = (clientX, clientY) => {
      if (!sliderRef.value) return props.modelValue

      const rect = sliderRef.value.getBoundingClientRect()
      let percentage

      if (props.orientation === 'horizontal') {
        const relativeX = clientX - rect.left
        percentage = relativeX / rect.width
      } else {
        const relativeY = clientY - rect.top
        percentage = 1 - (relativeY / rect.height) // Invert for vertical
      }

      percentage = Math.max(0, Math.min(1, percentage))
      const range = props.max - props.min
      return props.min + (percentage * range)
    }

    // Mouse event handlers
    const onSliderMouseDown = (event) => {
      if (props.disabled || props.readonly) return
      
      event.preventDefault()
      const newValue = getValueFromPosition(event.clientX, event.clientY)
      updateValue(newValue)
      
      // Start dragging from clicked position
      startDrag(event.clientX, event.clientY, newValue)
    }

    const onHandleMouseDown = (event) => {
      if (props.disabled || props.readonly) return
      
      event.preventDefault()
      event.stopPropagation()
      startDrag(event.clientX, event.clientY, props.modelValue)
    }

    const onMouseMove = (event) => {
      if (!isDragging.value) return
      
      event.preventDefault()
      const newValue = getValueFromPosition(event.clientX, event.clientY)
      updateValue(newValue)
    }

    const onMouseUp = () => {
      if (isDragging.value) {
        stopDrag()
      }
    }

    // Touch event handlers
    const onSliderTouchStart = (event) => {
      if (props.disabled || props.readonly) return
      
      event.preventDefault()
      const touch = event.touches[0]
      const newValue = getValueFromPosition(touch.clientX, touch.clientY)
      updateValue(newValue)
      
      startDrag(touch.clientX, touch.clientY, newValue)
    }

    const onHandleTouchStart = (event) => {
      if (props.disabled || props.readonly) return
      
      event.preventDefault()
      event.stopPropagation()
      const touch = event.touches[0]
      startDrag(touch.clientX, touch.clientY, props.modelValue)
    }

    const onTouchMove = (event) => {
      if (!isDragging.value) return
      
      event.preventDefault()
      const touch = event.touches[0]
      const newValue = getValueFromPosition(touch.clientX, touch.clientY)
      updateValue(newValue)
    }

    const onTouchEnd = () => {
      if (isDragging.value) {
        stopDrag()
      }
    }

    // Drag management
    const startDrag = (clientX, clientY, value) => {
      isDragging.value = true
      startPosition.value = props.orientation === 'horizontal' ? clientX : clientY
      startValue.value = value
      
      emit('slidestart', { value: props.modelValue })
      
      // Add global event listeners
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
      document.addEventListener('touchmove', onTouchMove, { passive: false })
      document.addEventListener('touchend', onTouchEnd)
      
      // Focus the handle
      if (handleRef.value) {
        handleRef.value.focus()
      }
    }

    const stopDrag = () => {
      isDragging.value = false
      
      emit('slideend', { value: props.modelValue })
      
      // Remove global event listeners
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
    }

    // Keyboard navigation
    const onKeyDown = (event) => {
      if (props.disabled || props.readonly) return

      let handled = false
      const step = props.step || 1
      const pageStep = step * 10
      let newValue = props.modelValue

      switch (event.code) {
        case 'ArrowRight':
        case 'ArrowUp':
          newValue = props.modelValue + step
          handled = true
          break
          
        case 'ArrowLeft':
        case 'ArrowDown':
          newValue = props.modelValue - step
          handled = true
          break
          
        case 'PageUp':
          newValue = props.modelValue + pageStep
          handled = true
          break
          
        case 'PageDown':
          newValue = props.modelValue - pageStep
          handled = true
          break
          
        case 'Home':
          newValue = props.min
          handled = true
          break
          
        case 'End':
          newValue = props.max
          handled = true
          break
      }

      if (handled) {
        event.preventDefault()
        updateValue(newValue)
      }
    }

    // Focus handlers
    const onHandleFocus = () => {
      isFocused.value = true
    }

    const onHandleBlur = () => {
      isFocused.value = false
    }

    // Cleanup on unmount
    onUnmounted(() => {
      if (isDragging.value) {
        stopDrag()
      }
    })

    // Watch for external value changes
    watch(() => props.modelValue, (newValue, oldValue) => {
      if (newValue !== oldValue && !isDragging.value) {
        // Normalize external value changes
        const normalizedValue = normalizeValue(newValue)
        if (normalizedValue !== newValue) {
          nextTick(() => {
            emit('update:modelValue', normalizedValue)
          })
        }
      }
    })

    return {
      sliderRef,
      handleRef,
      sliderClasses,
      handleClasses,
      rangeStyle,
      handleStyle,
      tooltipStyle,
      formattedValue,
      ariaValueText,
      onSliderMouseDown,
      onSliderTouchStart,
      onHandleMouseDown,
      onHandleTouchStart,
      onHandleFocus,
      onHandleBlur,
      onKeyDown
    }
  }
}
</script>

<style scoped>
/* Base slider styles */
.ui-slider-root {
  @apply relative bg-gray-200 rounded-full cursor-pointer select-none outline-none;
  touch-action: none;
}

.ui-slider-horizontal {
  @apply w-full h-2;
}

.ui-slider-vertical {
  @apply h-64 w-2;
}

.ui-slider-root:hover {
  @apply bg-gray-300;
}

.ui-slider-root.ui-slider-focused {
  @apply ring-2 ring-blue-500 ring-opacity-50;
}

.ui-slider-root.ui-slider-disabled {
  @apply opacity-50 cursor-not-allowed;
}

.ui-slider-root.ui-slider-readonly {
  @apply cursor-default;
}

/* Track */
.ui-slider-track {
  @apply absolute inset-0 rounded-full overflow-hidden;
}

/* Range (filled portion) */
.ui-slider-range {
  @apply absolute bg-blue-500 rounded-full transition-all duration-150 ease-out;
}

.ui-slider-horizontal .ui-slider-range {
  @apply h-full top-0 left-0;
}

.ui-slider-vertical .ui-slider-range {
  @apply w-full left-0;
}

.ui-slider-root:hover .ui-slider-range {
  @apply bg-blue-600;
}

.ui-slider-root.ui-slider-disabled .ui-slider-range {
  @apply bg-gray-400;
}

/* Handle */
.ui-slider-handle-root {
  @apply absolute w-5 h-5 bg-white border-2 border-blue-500 rounded-full cursor-grab outline-none transition-all duration-150 ease-out;
  top: 50%;
  transform: translateY(-50%);
}

.ui-slider-handle-root:hover {
  @apply border-blue-600 shadow-md;
  transform: translateY(-50%) scale(1.1);
}

.ui-slider-handle-root.ui-slider-handle-active {
  @apply cursor-grabbing shadow-lg border-blue-700;
  transform: translateY(-50%) scale(1.15);
}

.ui-slider-handle-root.ui-slider-handle-focused {
  @apply ring-2 ring-blue-500 ring-opacity-50;
}

.ui-slider-vertical .ui-slider-handle-root {
  left: 50%;
  transform: translateX(-50%);
}

.ui-slider-vertical .ui-slider-handle-root:hover {
  transform: translateX(-50%) scale(1.1);
}

.ui-slider-vertical .ui-slider-handle-root.ui-slider-handle-active {
  transform: translateX(-50%) scale(1.15);
}

.ui-slider-root.ui-slider-disabled .ui-slider-handle-root {
  @apply border-gray-400 cursor-not-allowed;
}

.ui-slider-root.ui-slider-disabled .ui-slider-handle-root:hover {
  transform: translateY(-50%);
}

/* Handle inner circle for better visual feedback */
.ui-slider-handle-inner {
  @apply w-2 h-2 bg-blue-500 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-150;
}

.ui-slider-handle-root:hover .ui-slider-handle-inner {
  @apply bg-blue-600;
}

.ui-slider-handle-root.ui-slider-handle-active .ui-slider-handle-inner {
  @apply bg-blue-700;
}

.ui-slider-root.ui-slider-disabled .ui-slider-handle-inner {
  @apply bg-gray-400;
}

/* Tooltip */
.ui-slider-tooltip {
  @apply absolute bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none;
  bottom: 100%;
  margin-bottom: 8px;
  z-index: 1000;
}

.ui-slider-tooltip::after {
  content: '';
  @apply absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800;
}

.ui-slider-vertical .ui-slider-tooltip {
  left: 100%;
  bottom: auto;
  margin-left: 8px;
  margin-bottom: 0;
}

.ui-slider-vertical .ui-slider-tooltip::after {
  top: 50%;
  left: 0;
  transform: translateY(-50%) translateX(-100%);
  @apply border-t-transparent border-r-gray-800 border-l-0;
}

/* Transitions for smooth interactions */
.ui-slider-root.ui-slider-dragging .ui-slider-range,
.ui-slider-root.ui-slider-dragging .ui-slider-handle-root {
  transition: none;
}

/* Focus styles for accessibility */
.ui-slider-root:focus-visible {
  @apply ring-2 ring-blue-500 ring-opacity-50;
}

.ui-slider-handle-root:focus-visible {
  @apply ring-2 ring-blue-500 ring-opacity-50;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .ui-slider-root {
    @apply border border-gray-600;
  }
  
  .ui-slider-range {
    @apply bg-blue-700;
  }
  
  .ui-slider-handle-root {
    @apply border-2 border-gray-800;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .ui-slider-range,
  .ui-slider-handle-root,
  .ui-slider-handle-inner {
    transition: none;
  }
}
</style> 